import { useSearchParams } from "react-router-dom"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import PageMainContainer from "@/components/Templates/PageMainContainer"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { cn } from "@/utils/utils"
import { MentoringModal } from "@/modules/Mentorings/components/MentoringModal"
import { MentoringFeedbackModal } from "@/modules/Mentorings/components/MentoringFeedbackModal"
import { MentoringsTabContent } from "@/modules/Mentorings/components/MentoringsTabContent"
import { MentorsTabContent } from "@/modules/Mentorings/components/MentorsTabContent"
import { useMentoringsPage } from "@/modules/Mentorings/hooks/useMentoringsPage"
// Plano de Ação oculto do backoffice — aba e import comentados junto com o restante abaixo.
// import ActionPlansPage from "@/modules/ActionPlans/pages/ActionPlansPage"

type ActiveTab = "mentors" | "mentorings" /* | "action-plans" */

export default function Mentoring() {
  const { user } = useAuth()

  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get("tab") as ActiveTab) || "mentorings"
  const setActiveTab = (tab: ActiveTab) => setSearchParams({ tab })

  const {
    mentores,
    mentoresForbidden,
    allMentorings,
    pendingMentoring,
    disabledAlert,
    isScheduledOpen,
    setIsScheduledOpen,
    highlightedMentoringId,
    isMentoringModalOpen,
    selectedCalLink,
    openBookingModal,
    closeBookingModal,
    handleBookingSuccess,
    isMentoringFeedbackModalOpen,
    openPendingFeedbackModal,
    closePendingFeedbackModal,
    submitPendingFeedback,
    isHistoryFeedbackModalOpen,
    selectedMentoringForHistoryFeedback,
    openHistoryFeedbackModal,
    closeHistoryFeedbackModal,
    submitHistoryFeedback,
  } = useMentoringsPage(user)

  const tabButtonClass = (tab: ActiveTab) =>
    cn(
      "py-2 px-4 font-semibold text-sm transition-colors",
      activeTab === tab
        ? "border-b-2 border-primary text-primary"
        : "text-foreground/60 hover:text-foreground"
    )

  return (
    <div className="w-full">
      <PageHeader
        title="Mentorias"
        icon="handshake"
      />
      <PageMainContainer>
        <div className="w-full">
          {mentoresForbidden ? (
            <div className="flex w-full flex-wrap items-center justify-between gap-4 rounded-sm border border-red-500 bg-red-950 p-4">
              <div>
                <h6 className="text-[1.2rem] font-bold">Atenção!</h6>
                <p>
                  Há algo de errado com a validade do seu contrato. Contate seu GS{" "}
                  <strong>{user?.gs_nome}</strong>.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex border-b border-foreground/10">
                <button
                  className={tabButtonClass("mentorings")}
                  onClick={() => setActiveTab("mentorings")}
                >
                  Mentorias
                </button>
                <button
                  className={tabButtonClass("mentors")}
                  onClick={() => setActiveTab("mentors")}
                >
                  Agendar mentoria
                </button>
                {/* Plano de Ação oculto do backoffice.
                <button
                  className={tabButtonClass("action-plans")}
                  onClick={() => setActiveTab("action-plans")}
                >
                  Planos de ação
                </button>
                */}
              </div>
              <section className="pt-2">
                {activeTab === "mentorings" && (
                  <MentoringsTabContent
                    allMentorings={allMentorings}
                    highlightedMentoringId={highlightedMentoringId}
                    isScheduledOpen={isScheduledOpen}
                    onToggleScheduledOpen={() => setIsScheduledOpen(!isScheduledOpen)}
                    onRate={openHistoryFeedbackModal}
                  />
                )}
                {activeTab === "mentors" && (
                  <MentorsTabContent
                    mentores={mentores}
                    disabledAlert={disabledAlert}
                    onRateLastMentoring={openPendingFeedbackModal}
                    onSchedule={openBookingModal}
                  />
                )}
                {/* {activeTab === "action-plans" && <ActionPlansPage />} */}
              </section>
            </>
          )}
        </div>
      </PageMainContainer>

      <MentoringModal
        isOpen={isMentoringModalOpen}
        onClose={closeBookingModal}
        calLink={selectedCalLink}
        onBookingSuccess={async () => {
          setActiveTab("mentorings")
          await handleBookingSuccess()
        }}
      />

      {pendingMentoring && (
        <MentoringFeedbackModal
          isOpen={isMentoringFeedbackModalOpen}
          onClose={closePendingFeedbackModal}
          mentoring={pendingMentoring}
          onSubmit={submitPendingFeedback}
        />
      )}

      {selectedMentoringForHistoryFeedback && (
        <MentoringFeedbackModal
          isOpen={isHistoryFeedbackModalOpen}
          onClose={closeHistoryFeedbackModal}
          mentoring={selectedMentoringForHistoryFeedback}
          onSubmit={submitHistoryFeedback}
        />
      )}
    </div>
  )
}
